import { FranchiseFeatureType } from '../franchises/type';
import {
  UberallAiTone,
  UberallAutoResponseTrigger,
  UberallCompletenessLevel,
  UberallCustomerActionsPayload,
  UberallListingStatus,
  UberallScoreComponent,
  UberallSocialConnectionsPayload,
  UberallSocialPostType,
} from './api/contract';
import { ComparisonPreset, StatsRangePreset } from './type';

export const USE_MOCK = process.env.REACT_APP_VISIBILITY_USE_MOCK === 'true';

/**
 * The real RBAC gate — always on, independent of USE_MOCK. Mocking the data a merchant sees must
 * never imply skipping the check for whether they're allowed to see it at all (US-01: previously
 * tied to USE_MOCK, which meant every owner saw the module regardless of Uberall linkage).
 */
export const VISIBILITY_FEATURE: FranchiseFeatureType = FranchiseFeatureType.UBERALL;

/**
 * Independent of USE_MOCK, same reasoning as VISIBILITY_FEATURE above: whether a merchant
 * can add/edit service areas locally has nothing to do with whether this environment is
 * mocked. Manual placeId entry (via Google's public lookup tool) works with zero Google
 * API access, so this stays true everywhere.
 */
export const SERVICE_AREAS_WRITABLE = true;

/** Uberall's own per-location cap — enforced on the *ready* (placeId-present) subset at
 * sync time, not on the local list a merchant can freely add to. */
export const MAX_SERVICE_AREAS = 20;

/** The local list's own cap, deliberately looser than MAX_SERVICE_AREAS: a merchant can jot
 * down more candidate zones than can ever be live on Google at once. */
export const MAX_LOCAL_SERVICE_AREAS = 40;

/** Google's own free, public, no-API-key place-id lookup tool — the only way to attach a
 * real placeId to a local-only service area without any Google API access. */
export const GOOGLE_PLACE_ID_LOOKUP_URL =
  'https://developers.google.com/maps/documentation/places/web-service/place-id';

export const UBERALL_SERVICES_PATH = 'services/v2/uberall';

export const STORES_PATH = 'api/v2/stores';

export const REVIEWS_ROWS_PER_PAGE_OPTIONS = [5, 10, 20];
export const DEFAULT_REVIEWS_ROWS_PER_PAGE = 10;

export const REVIEW_REPLY_MAX_LENGTH = 4000;

export const AI_REPLY_INSTRUCTION_MAX_LENGTH = 500;

export const DESCRIPTION_SHORT_MAX_LENGTH = 200;
export const DESCRIPTION_LONG_MAX_LENGTH = 750;

export const IMPRINT_MAX_LENGTH = 20000;

export const AI_TONES: UberallAiTone[] = ['warm_artisanal', 'professional_neutral', 'friendly_casual'];
export const DEFAULT_AI_TONE: UberallAiTone = 'warm_artisanal';

export const AI_LANGUAGE_AUTO = 'auto';
export const AI_LANGUAGES = [AI_LANGUAGE_AUTO, 'fr', 'en'];

export const AI_SIGNATURE_MAX_LENGTH = 120;

export const REPLY_TEMPLATE_EMOJIS = ['😊', '🙏', '⭐', '💬', '🎉', '🤝', '✨', '🛠️'];

export const MAX_REPLY_TEMPLATES = 30;
export const TEMPLATE_LABEL_MAX_LENGTH = 80;
export const TEMPLATE_TEXT_MAX_LENGTH = 4000;
export const TEMPLATE_THEME_MAX_LENGTH = 80;

export const AUTO_RESPONSE_TRIGGERS: UberallAutoResponseTrigger[] = [
  'FIVE_STAR',
  'FOUR_STAR',
  'THREE_STAR',
  'TWO_STAR',
  'ONE_STAR',
];

export const AUTO_RESPONSE_TRIGGER_RATING: Record<UberallAutoResponseTrigger, number> = {
  ONE_STAR: 1,
  TWO_STAR: 2,
  THREE_STAR: 3,
  FOUR_STAR: 4,
  FIVE_STAR: 5,
};

export const AUTO_RESPONSE_VARIANT_COUNT = 3;

export const AUTO_RESPONSE_MIN_UNATTENDED_RATING = 4;

export const AUTO_RESPONSE_NAME_MAX_LENGTH = 200;
export const AUTO_RESPONSE_TEXT_MAX_LENGTH = 4000;

export const CUSTOMER_ACTION_KEYS: (keyof UberallCustomerActionsPayload)[] = [
  'calls',
  'directions',
  'websiteVisits',
  'clickAndCollect',
];

export const SEO_REPORT_SENDER = 'contact@littlebill.eu';
export const SEO_REPORT_INTERNAL_COPIES = ['account@littlebill.eu', 'm.lemarchand@littlebill.eu'];

export const DEFAULT_DASHBOARD_PRESET: StatsRangePreset = 'LAST_30_DAYS';

export const COMPARISON_PRESETS: ComparisonPreset[] = ['PREVIOUS_PERIOD', 'PREVIOUS_YEAR', 'CUSTOM'];

export const RANGE_PRESETS: StatsRangePreset[] = [
  'LAST_7_DAYS',
  'LAST_30_DAYS',
  'LAST_90_DAYS',
  'LAST_6_MONTHS',
  'LAST_12_MONTHS',
];

export const COMPLETENESS_LEVEL_ORDER: UberallCompletenessLevel[] = ['REQUIRED', 'BASIC', 'ADVANCED', 'BONUS'];

export const LISTING_STATUS_ORDER: Record<UberallListingStatus, number> = {
  ACTION_REQUIRED: 0,
  UPDATING: 1,
  SUBMITTED: 2,
  SCHEDULED: 3,
  IN_PROGRESS: 4,
  NOT_STARTED: 5,
  UNKNOWN: 6,
  LINKED: 7,
  SYNCED: 8,
};

/** The settings tabs in render order; every tab index in the module derives from this. */
export const SETTINGS_TABS = ['profile', 'connectors', 'photos', 'keywords'] as const;

export type SettingsTabName = (typeof SETTINGS_TABS)[number];

export const settingsTabIndex = (tab: SettingsTabName) => SETTINGS_TABS.indexOf(tab);

export const SETTINGS_TAB_BY_TASK_TARGET: Partial<Record<string, SettingsTabName>> = {
  SETTINGS_PROFILE: 'profile',
  SETTINGS_CONNECTORS: 'connectors',
  SETTINGS_PHOTOS: 'photos',
  SETTINGS_KEYWORDS: 'keywords',
};

export const SCORE_TASK_EMOJI: Record<UberallScoreComponent, string> = {
  CONNECTORS: '⚠️',
  REVIEWS: '💬',
  PHOTOS: '🖼️',
  POSTS: '📄',
  COMPLETENESS: '📋',
};

export const SCORE_TASK_EMOJI_FALLBACK = '📌';

export const SETTINGS_TAB_QUERY_PARAM = 'tab';

export const VISIBILITY_REVIEWS_NAV_ID = 'visibility-reviews';

export const VISIBILITY_SCORE_NAV_ID = 'visibility-score';

/**
 * The two entries that carry a count.
 * Paramétrage and Posts deliberately carry none: their open tasks are already counted on the score,
 * so badging them too would show the same work twice.
 */
export const VISIBILITY_BADGE_NAV_IDS = [VISIBILITY_SCORE_NAV_ID, VISIBILITY_REVIEWS_NAV_ID];

export const AI_POST_TOPIC_MAX_LENGTH = 500;

/** Uberall's cap on a post image; the backend refuses above it too. */
export const POST_VISUAL_MAX_BYTES = 4 * 1024 * 1024;

/** What Google accepts on a post; also what the file input filters to. */
export const POST_VISUAL_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export const POST_DESCRIPTION_MAX_LENGTH = 1500;
export const POST_TITLE_MAX_LENGTH = 300;

export const POST_TYPES: UberallSocialPostType[] = ['POST', 'OFFER', 'EVENT', 'ALERT'];
export const DEFAULT_POST_TYPE: UberallSocialPostType = 'POST';

// Directories shown first in the post composer, in this order, when the location offers them.
export const MAIN_POST_DIRECTORIES = ['GOOGLE', 'FACEBOOK', 'INSTAGRAM', 'APPLE_MAPS', 'LINKED_IN'];
export const DEFAULT_POST_DIRECTORIES = ['GOOGLE'];
/** Other names Uberall uses for a main directory, mapped to the one MAIN_POST_DIRECTORIES carries. */
export const MAIN_POST_DIRECTORY_ALIASES: Record<string, string> = {
  GOOGLE_MAPS: 'GOOGLE',
  APPLE: 'APPLE_MAPS',
  LINKEDIN: 'LINKED_IN',
};
/** The single key under which Uberall reports the status of every secondary directory of a post. */
export const OTHER_DIRECTORIES_STATUS_KEY = 'OTHER_DIRECTORIES';
// Offered by Uberall but not opened to merchants yet: shown greyed out, never sent.
export const COMING_SOON_POST_DIRECTORIES = ['APPLE_MAPS', 'LINKED_IN'];

/** Directories Uberall can only post to once the merchant has connected the account behind them. */
export const SOCIAL_CONNECTION_BY_DIRECTORY: Partial<Record<string, keyof UberallSocialConnectionsPayload>> = {
  FACEBOOK: 'facebook',
  INSTAGRAM: 'instagram',
};

// Offers, events and alerts are Google formats: Facebook and Instagram take plain posts only.
export const SOCIAL_POST_TYPES: UberallSocialPostType[] = ['POST'];

export interface SocialPostVisualRule {
  types: string[];
  maxBytes: number;
  isRequired: boolean;
}

/** Uberall's media rules for the social directories, stricter than what Google takes (POST_VISUAL_*). */
export const SOCIAL_POST_VISUAL_RULES: Partial<Record<string, SocialPostVisualRule>> = {
  FACEBOOK: {
    types: ['image/jpeg', 'image/png', 'image/bmp', 'image/tiff'],
    maxBytes: 4 * 1024 * 1024,
    isRequired: false,
  },
  INSTAGRAM: { types: ['image/jpeg'], maxBytes: 4 * 1024 * 1024, isRequired: true },
};

export const SOCIAL_CONNECT_POLL_MS = 3000;
export const SOCIAL_CONNECT_TIMEOUT_MS = 10 * 60 * 1000;
export const SOCIAL_DISCONNECT_RECHECK_MS = 3000;

export const DIRECTORY_COLORS: Record<string, string> = {
  GOOGLE: '#4285F4',
  FACEBOOK: '#1877F2',
  INSTAGRAM: '#E1306C',
  APPLE: '#111111',
  APPLE_MAPS: '#111111',
  LINKEDIN: '#0A66C2',
  LINKED_IN: '#0A66C2',
  BING: '#008373',
  YELP: '#D32323',
  TRIPADVISOR: '#34E0A1',
  FOURSQUARE: '#F94877',
  WAZE: '#33CCFF',
  HERE: '#48DAD0',
  TOMTOM: '#DF1B12',
  MAPPY: '#E2001A',
  VIAMICHELIN: '#27509B',
  NEXTDOOR: '#8ED500',
  PAGESJAUNES: '#FFE000',
  CYLEX: '#F7941E',
  PAGES24: '#D9480F',
  HOTFROG: '#F2542D',
  PRATIQUE: '#0B72B9',
  FIND_OPEN: '#E8467C',
  NAVMII: '#2E9E5B',
  SHOWMELOCAL: '#F5A623',
  WO_GIBTS_WAS: '#2F6FB7',
};

export const DIRECTORY_LOGOS: Record<string, string> = {
  GOOGLE: '/assets/images/logos/google.svg',
  GOOGLE_MAPS: '/assets/images/logos/google.svg',
  FACEBOOK: '/assets/images/logos/facebook.png',
  INSTAGRAM: '/assets/images/logos/instagram.svg',
  APPLE: '/assets/images/logos/apple.svg',
  APPLE_MAPS: '/assets/images/logos/apple.svg',
  SIRI: '/assets/images/logos/apple.svg',
  LINKEDIN: '/assets/images/logos/linkedin.svg',
  LINKED_IN: '/assets/images/logos/linkedin.svg',
  BING: '/assets/images/logos/bing.svg',
  YELP: '/assets/images/logos/yelp.svg',
  TRIPADVISOR: '/assets/images/logos/tripadvisor.svg',
  FOURSQUARE: '/assets/images/logos/foursquare.svg',
  WAZE: '/assets/images/logos/waze.svg',
  HERE: '/assets/images/logos/here.svg',
  NOKIA_HERE: '/assets/images/logos/here.svg',
  TOMTOM: '/assets/images/logos/tomtom.svg',
  MAPPY: '/assets/images/logos/mappy.svg',
  VIAMICHELIN: '/assets/images/logos/viamichelin.svg',
  NEXTDOOR: '/assets/images/logos/nextdoor.svg',
  PAGESJAUNES: '/assets/images/logos/pagesjaunes.svg',
  CYLEX: '/assets/images/logos/cylex.svg',
};

// Uberall syncs to some forty directories and sends no logo for any of them (logoUrl is
// null): the rest show the directory site's own icon, and the monogram if that fails too.
export const DIRECTORY_SITES: Record<string, string> = {
  ACOMPIO: 'acompio.de',
  ALEXA: 'alexa.amazon.com',
  ANNUAIRE: 'annuaire.com',
  AUDI: 'audi.com',
  BMW: 'bmw.fr',
  CHAT_GPT: 'chatgpt.com',
  CLAUDE: 'claude.ai',
  FIAT: 'fiat.com',
  FIND_OPEN: 'find-open.com',
  FORD: 'ford.com',
  GEMINI: 'gemini.google.com',
  GM: 'gm.com',
  HOTFROG: 'hotfrog.fr',
  HUAWEI: 'huawei.com',
  INFOBEL: 'infobel.com',
  INFO_IS_INFO: 'infoisinfo.fr',
  MAP_QUEST: 'mapquest.com',
  MERCEDES: 'mercedes-benz.com',
  NAVMII: 'navmii.com',
  OEFFNUNGSZEITENBUCH: 'oeffnungszeitenbuch.de',
  PAGES24: 'pages24.com',
  PERPLEXITY: 'perplexity.ai',
  PRATIQUE: '118000.fr',
  SHOWMELOCAL: 'showmelocal.com',
  STADTBRANCHENBUCH: 'opendi.fr',
  TOYOTA: 'toyota.com',
  TUPALO: 'tupalo.com',
  UBER: 'uber.com',
  VW: 'vw.com',
  WO_GIBTS_WAS: 'wogibtswas.de',
};

export const DIRECTORY_SITE_ICON_URL = 'https://www.google.com/s2/favicons?sz=64&domain=';

export const GOOGLE_POST_STALE_DAYS = 21;

export const REVIEW_RATING_SCALE = 5;
